import _ from 'lodash'
import Guard from 'arg-guard'

import Logger from '@/logger'

import FILTER_NAMES from './FILTER_NAMES'
import METRIC_KEYS from '@/models/METRIC_KEYS'

const loggingCategory = 'Filters'

/*
 * The Plan Builder Manager Filters module is a subset of methods to filter improvement sets.
 * IMPORTANT: Each Filter method must take an improvements array and a params object so that hey can be dynamically dispatched.
 */
const PlanBuilderFilters = {

    /**
     * Selects the improvements that will attain a SAP C Score.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {boolean} [includeAlts=false] Include the alts for a sort operation.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.aim4SapC](improvements, { includeAlts } = { includeAlts: false }) {
        Guard.throwIf(improvements, 'improvements')

        let sapCurrent = 0
        const sapTarget = 69
        const errorMarginFactorOnImprovementAdditives = 0.85 // 15% reduction to allow for margin of savings
        const blacklist = []
        const filteredImprovements = []

        /**
         * @param {Improvement} item
         * @returns {boolean}
         */
        const isEfficient = function(item) {
            return item.efficiencies && item.efficiencies.sapImprovement > 0 || item.efficiencies.costPerSapScoreImprovement
        }

        Logger.logObject({ improvements }, 'Aim for SAP C', loggingCategory)
        try {
            /** @type {Array<Improvement>} **/
            const sapImprovements = this[FILTER_NAMES.improveEpc](improvements, { includeAlts: includeAlts })

            if (!sapImprovements.length)
                return filteredImprovements

            // Initialise starting SAP score when value becomes available.
            const elSapChange = sapImprovements[0].metrics.find(m => m.key === METRIC_KEYS.sapScore)
            sapCurrent = elSapChange ? elSapChange.current.value : 0

            for (let i = 0; i < sapImprovements.length; i++) {
                const el = sapImprovements[i]

                if (sapCurrent < sapTarget && !blacklist.includes(el.id) && isEfficient(el)) {
                    filteredImprovements.push(_.cloneDeep(el))
                    const sapChangeValue = el.efficiencies.sapImprovement * errorMarginFactorOnImprovementAdditives
                    sapCurrent = sapCurrent + sapChangeValue

                    const alts = PlanBuilderFilters.alternativesTo(sapImprovements, { item: el, disableLogging: true })
                    blacklist.push(...alts.map(item => item.id))
                }

                if (sapCurrent >= sapTarget)
                    return filteredImprovements
            }
            return filteredImprovements
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.aim4SapC } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that will reduce your carbon emissions in impact towards zero carbon order.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {boolean} [includeAlts=false] Include the alts for a sort operation.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.aim4ZeroCarbon](improvements, { includeAlts } = { includeAlts: false }) {
        Guard.throwIf(improvements, 'improvements')

        Logger.logObject({ improvements }, 'Aim for Zero carbon', loggingCategory)
        try {
            const filtered = improvements.filter(i => i.savings && i.savings.carbonDioxide && i.efficiencies)
            const sorted = _.sortBy(filtered, item => -item.efficiencies.co2)
            return includeAlts ? sorted : PlanBuilderFilters.filterOutAlternativesFromSortedImprovements(sorted)
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.aim4ZeroCarbon } filter (${ err.message })`)
        }
    },

    /**
     * Gets the actual alternative data from the item alternatives.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {Improvement} item The improvement to look for alternatives against.
     * @param {Boolean} [disableLogging=false] If set, this disables logging - prevents verbose logging when this is used as a child filter.
     * @returns {Array<ImprovementBasics>|Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.alternativesTo](improvements, { item, disableLogging = false } = { item: null, disableLogging: false }) {
        Guard.throwIf(improvements, 'improvements')
        Guard.throwIf(item, 'item')

        if (!disableLogging) Logger.logObject({ improvements, item }, 'Alternatives To', loggingCategory)

        try {
            // noinspection JSValidateTypes
            return (item.alternatives ? item.alternatives : [])
                .map(alt => improvements.find(imp => imp.id === alt.id))
                .filter(i => i)

        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.alternativesTo } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that will improve the SAP Score.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {boolean} [includeAlts=false] Include the alts for a sort operation.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.improveEpc](improvements, { includeAlts } = { includeAlts: false }) {
        Guard.throwIf(improvements, 'improvements')

        Logger.logObject({ improvements }, 'Improve EPC', loggingCategory)
        try {
            const filtered = improvements.filter(i => i.savings && i.efficiencies.sapImprovement && i.efficiencies.sapImprovement > 0)
            const sorted = _.sortBy(filtered, item => item.efficiencies.costPerSapScoreImprovement)
            return includeAlts ? sorted : PlanBuilderFilters.filterOutAlternativesFromSortedImprovements(sorted)
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.improveEpc } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that will reduce financial and environmental costs in efficiency order.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {boolean} [includeAlts=false] Include the alts for a sort operation.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.mostEfficient](improvements, { includeAlts } = { includeAlts: false }) {
        Guard.throwIf(improvements, 'improvements')

        Logger.logObject({ improvements }, 'Most Efficient', loggingCategory)
        try {
            const filtered = improvements.filter(i => i.efficiencies)
            const sorted = _.sortBy(filtered, item => -item.efficiencies.effectivenessRating)
            return includeAlts ? sorted : PlanBuilderFilters.filterOutAlternativesFromSortedImprovements(sorted)
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.mostEfficient } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that are recommended.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.recommended](improvements) {
        Guard.throwIf(improvements, 'improvements')

        Logger.logObject({ improvements }, 'Recommended', loggingCategory)
        try {
            return improvements.filter(imp => imp.recommended)
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.recommended } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that will reduce your carbon emissions in efficiency order.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {boolean} [includeAlts=false] Include the alts for a sort operation.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.reduceCo2Emissions](improvements, { includeAlts } = { includeAlts: false }) {
        Guard.throwIf(improvements, 'improvements')

        Logger.logObject({ improvements }, 'Reduce CO₂ Emissions', loggingCategory)
        try {
            const filtered = improvements.filter(i => i.savings && i.savings.carbonDioxide && i.efficiencies)
            const sorted = _.sortBy(filtered, item => -item.efficiencies.co2PerPound)
            return includeAlts ? sorted : PlanBuilderFilters.filterOutAlternativesFromSortedImprovements(sorted)
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.reduceCo2Emissions } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that will reduce your fuel bill in efficiency order.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {boolean} [includeAlts=false] Include the alts for a sort operation.
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.reduceFuelBill](improvements, { includeAlts } = { includeAlts: false }) {
        Guard.throwIf(improvements, 'improvements')

        Logger.logObject({ improvements }, 'Reduce Fuel Bill', loggingCategory)
        try {
            const filtered = improvements.filter(i => i.savings && i.savings.fuelBill && i.efficiencies)
            const sorted = _.sortBy(filtered, item => item.efficiencies.payback)
            return includeAlts ? sorted : PlanBuilderFilters.filterOutAlternativesFromSortedImprovements(sorted)
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.reduceFuelBill } filter (${ err.message })`)
        }
    },

    /**
     * Selects the improvements that can be delivered with the current budget.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @param {?number} [budget=null]
     * @returns {Array<Improvement>} The improvements that match the applied filter.
     */
    [FILTER_NAMES.withinBudget](improvements, { budget } = { budget: null }) {
        Guard.throwIf(improvements, 'improvements')
        Guard.throwIf(budget, 'budget')

        let availableBudget = budget
        const blacklist = []
        const filteredImprovements = []

        /**
         * @param {Improvement} item
         * @returns {boolean}
         */
        const isEfficient = function(item) {
            return item.efficiencies && item.efficiencies.payback || item.efficiencies.co2PerPound > 0
        }

        Logger.logObject({ improvements, budget, blacklist }, 'Within Budget', loggingCategory)
        try {
            for (let i = 0; i < improvements.length; i++) {
                const el = improvements[i]
                if (el.price <= availableBudget && !blacklist.includes(el.id) && isEfficient(el)) {
                    filteredImprovements.push(_.cloneDeep(el))
                    availableBudget = availableBudget - el.price
                    const alts = PlanBuilderFilters.alternativesTo(improvements, { item: el, disableLogging: true })
                    blacklist.push(...alts.map(item => item.id))
                }
            }
            return filteredImprovements
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the ${ FILTER_NAMES.withinBudget } filter (${ err.message })`)
        }
    },

    /**
     * Filters out any alternatives from a previously sorted list of improvements, prioritising earlier improvements.
     * @param {Array<Improvement>} improvements The source improvements to filter.
     * @returns {Array<Improvement>} An alternative free improvements array.
     */
    filterOutAlternativesFromSortedImprovements(improvements) {
        const blacklist = []
        const filteredImprovements = []

        Logger.logObject({ improvements }, 'Filter Out Alternatives', loggingCategory)
        try {
            for (let i = 0; i < improvements.length; i++) {
                const el = improvements[i]
                if (!blacklist.includes(el.id)) {
                    filteredImprovements.push(_.cloneDeep(el))
                    blacklist.push(...PlanBuilderFilters.alternativesTo(improvements, { item: el, disableLogging: true }).map(item => item.id))
                }
            }

            return filteredImprovements
        } catch (err) {
            throw new Error(`Filter failure: An error occurred applying the filterOutAlternatives filter (${ err.message })`)
        }
    },
}

export default PlanBuilderFilters
